import React from 'react'
import { useDispatch, addNotification } from '@ggs/store'
import { useI18n } from '@ggs/i18n'
import { get } from 'lodash'
import { useApiRequest } from './'

/**
 * @typedef {import('../../definitions').WorkerRecord} WorkerRecord
 * @typedef {import('../../definitions').WorkerListParams} WorkerListParams
 * @typedef {import('../../definitions').WorkersListResponse} WorkerListResponse
 */

/**
 * @typedef {(id: string) => Promise<WorkerRecord>} GetWorker
 * @typedef {(params: WorkerListParams) => Promise<WorkerListResponse>} GetWorkers
 */

/**
 * @typedef {Object} HcmApiHook
 * @property {GetWorker} getWorker
 * @property {GetWorkers} getWorkers
 */

/**
 * Hook that expose functions to consume HCM API data.
 * @return {HcmApiHook}
 */
function useHcmApi() {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const baseUrl = `${process.env.REACT_APP_HCM_BASE_URL}` || 'http://localhost'
  const { doApiRequest, prepareQueryParams } = useApiRequest({
    baseUrl,
  })

  /**
   * Process a API request.
   * @param {string} endpoint An API endpoint path
   * @param {any} data Payload data
   * @param {Object<string, string>=} customHeaders
   * @param {string} method
   * @return {Promise<any>}
   */
  const doRequest = async (endpoint, data = null, customHeaders, method = '') => {
    const proxyEndpoint = `?_api_proxy_uri=${encodeURIComponent(endpoint)}`
    const response = doApiRequest({
      endpoint: proxyEndpoint,
      data,
      customHeaders,
      method,
    })

    const errorCode = get(response, 'errorCode', '')
    if (errorCode) {
      dispatch(
        addNotification(
          'hcm_api_request_failed',
          'error',
          t('HCM API request failed.')
        )
      )

      return null
    }

    return response
  }

  /**
   * Get worker record.
   * @type {GetWorker}
   * @param {string} workerId
   * @return {Promise<WorkerRecord>}
   */
  const getWorker = async (workerId) => {
    const endpoint = `/workers?refId=${workerId}&pageSize=1&page=1`
    const results = await doRequest(endpoint)
    const entity = get(results, 'pageItems[0]', null)

    if (!entity) {
      return null
    }

    return entity
  }

  /**
   * Get multiple worker records by employee IDs.
   * @type {GetWorkers}
   * @param {WorkerListParams} params
   * @return {Promise<WorkerListResponse>}
   */
  const getWorkers = async (params) => {
    const endpoint = '/workers'
    const queryParams = prepareQueryParams(params)
    const response = await doRequest(endpoint + '?' + queryParams)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  return {
    getWorker,
    getWorkers,
  }
}

export default useHcmApi
